/* eslint-disable @typescript-eslint/no-explicit-any */
// ============= Imports =============

import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

interface submitWorkflowType {
  workflowId: number;
  sectionKey: string;
  fieldKey: string;
  submittedData: any;
  getAccessToken: AuthContextProps['getAccessToken'];
  spoofedUser: string | null;
}

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_URL;
const API_ENDPOINT = '/api/Document/upload';
const AXIS_SCOPE = 'api://f23d5e95-70a8-4aca-b373-e23d8c9f6454/access_as_user';

/**
 * Creates headers for API request
 * @param token Authentication token
 * @param isFileUpload Whether this is a file upload request
 * @returns Headers object
 */
const createHeaders = (
  token: string,
  spoofedUser: string | null
): Record<string, string> => {
  const baseHeaders: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(spoofedUser && {
      'x-Spoof-User': spoofedUser,
    }),
  } as Record<string, string>;

  return baseHeaders;
};

// ============= Main Function =============
/**
 * Submits a single document or file to the API endpoint
 * @param workflowId The workflow identifier
 * @param sectionKey The section identifier
 * @param fieldKey The field identifier
 * @param submittedData The data to submit (single file or file object)
 * @param getAccessToken Function to acquire access token
 * @param spoofedUser Optional user to spoof for the request
 * @returns Promise with API response containing success status and data or error
 */
export const submitDocument = async ({
  workflowId,
  sectionKey,
  fieldKey,
  submittedData,
  getAccessToken,
  spoofedUser,
}: submitWorkflowType) => {
  try {
    console.log('🔄 Submitting single document...');

    // Token acquisition with required scopes
    const token = await getAccessToken([AXIS_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}?workflowId=${workflowId}&sectionKey=${sectionKey}&fieldKey=${fieldKey}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // Prepare request body; prefer FormData for file submissions
    let requestBody: string | FormData;

    // Check if submittedData contains file information
    if (submittedData && typeof submittedData === 'object') {
      // If it's a File object or has file-like properties
      if (
        submittedData instanceof File ||
        (submittedData.file && submittedData.file instanceof File)
      ) {
        const formData = new FormData();

        // Handle single file data structures
        if (submittedData instanceof File) {
          formData.append('file', submittedData);
        } else if (submittedData.file && submittedData.file instanceof File) {
          formData.append('file', submittedData.file);
          // Add any additional metadata
          if (submittedData.metadata) {
            formData.append('metadata', JSON.stringify(submittedData.metadata));
          }
        }

        requestBody = formData;
        console.log('📁 Preparing single file upload with FormData');
      } else {
        // Regular JSON data
        requestBody = JSON.stringify(submittedData);
        console.log('📄 Preparing JSON data submission');
      }
    } else {
      // Fallback to JSON
      // If no object or file info given, still submit an empty FormData to satisfy server expectations
      const formData = new FormData();
      requestBody = formData;
      console.log('📁 Preparing empty FormData submission (fallback)');
    }

    // Build headers so that for FormData we let the browser set the correct Content-Type with boundary
    const headers = createHeaders(token, spoofedUser || null);
    if (typeof requestBody === 'string') {
      headers['Content-Type'] = 'application/json';
    } // else let the browser set multipart/form-data with boundary

    // API request
    const response = await fetch(convertURI, {
      method: 'POST',
      body: requestBody,
      headers,
    });

    // Response handling
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      const fieldErrors: Record<string, string> = {};

      try {
        const contentType = response.headers.get('content-type') || '';
        let errorData: unknown = null;
        if (contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          errorData = await response.text();
        }

        if (typeof errorData === 'string') {
          const trimmed = errorData.trim();
          if (trimmed) {
            errorMessage = `${errorMessage}: ${trimmed}`;
          }
        } else if (errorData && typeof errorData === 'object') {
          const asRecord = errorData as Record<string, unknown>;
          // Some APIs use 'message' or 'title' for primary error text
          const primary = (asRecord.message || asRecord.title) as
            | string
            | undefined;
          if (typeof primary === 'string' && primary) {
            errorMessage = `${errorMessage}: ${primary}`;
          }
          if (asRecord.errors && typeof asRecord.errors === 'object') {
            const errorsObj = asRecord.errors as Record<string, unknown>;
            Object.entries(errorsObj).forEach(([field, messages]) => {
              if (Array.isArray(messages)) {
                fieldErrors[field] = messages.join(' ');
              } else if (typeof messages === 'string') {
                fieldErrors[field] = messages;
              }
            });
          }
        }
      } catch {
        errorMessage = `${errorMessage}: ${
          response.statusText || 'Unknown error'
        }`;
      }

      console.log(
        '❌ Single document submit failed:',
        errorMessage,
        fieldErrors
      );
      return {
        success: false,
        error: errorMessage,
        errors: fieldErrors,
        status: response.status,
      } as const;
    }

    let data: unknown = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    console.log('✅ Single document submitted successfully');

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('🔴 Error submitting single document:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
};
